import {
  fieldTypes,
  fieldValidationPatterns,
  fieldValidationMessages,
  meetingLogMandatorySubFields,
} from "@/src/constants/formConstants.js";

export function useFormValidation() {
  const validateField = (field, value) => {
    if (field.required && !value && value !== 0 && value !== false) {
      return {
        isValid: false,
        message: fieldValidationMessages.REQUIRED,
      };
    }

    if (!value && value !== 0 && value !== false) {
      return { isValid: true, message: "" };
    }

    switch (field.type) {
      case fieldTypes.EMAIL:
        if (!fieldValidationPatterns.EMAIL.test(value)) {
          return {
            isValid: false,
            message: fieldValidationMessages.EMAIL,
          };
        }
        break;

      case fieldTypes.URL:
        if (!fieldValidationPatterns.URL.test(value)) {
          return {
            isValid: false,
            message: fieldValidationMessages.URL,
          };
        }
        break;

      case fieldTypes.DOMAIN:
        if (!fieldValidationPatterns.DOMAIN.test(value)) {
          return {
            isValid: false,
            message: fieldValidationMessages.DOMAIN,
          };
        }
        break;

      case fieldTypes.PHONE:
        if (typeof value === "string" && !fieldValidationPatterns.PHONE.test(value)) {
          return {
            isValid: false,
            message: fieldValidationMessages.PHONE,
          };
        }
        break;

      case fieldTypes.NUMBER:
        if (typeof value !== "number" && isNaN(Number(value))) {
          return {
            isValid: false,
            message: "Please enter a valid number",
          };
        }
        break;

      case fieldTypes.MEETING_LOG: {
        if (!value || typeof value !== "object") {
          if (field.required) {
            return { isValid: false, message: fieldValidationMessages.REQUIRED };
          }
          break;
        }
        const requiredSubFields = meetingLogMandatorySubFields.filter((sf) => sf.required);
        for (const subField of requiredSubFields) {
          const subVal = value[subField.key];
          if (subVal === null || subVal === undefined || subVal === "") {
            return {
              isValid: false,
              message: `${subField.label} is required`,
            };
          }
          if (Array.isArray(subVal) && subVal.length === 0) {
            return {
              isValid: false,
              message: `${subField.label} is required`,
            };
          }
        }
        break;
      }

      default:
        break;
    }

    return { isValid: true, message: "" };
  };

  const getAllVisibleFields = (fields, fieldVisibility = {}) => {
    const visibleFields = [];

    const collectFields = (fieldsList) => {
      fieldsList.forEach((field) => {
        if (field.type === fieldTypes.SECTION) {
          if (field.children && field.children.length > 0) {
            collectFields(field.children);
          }
        } else if (
          field.type !== fieldTypes.TEXT_DISPLAY &&
          field.type !== fieldTypes.IMAGE
        ) {
          const isVisible =
            fieldVisibility[field.id] !== undefined
              ? fieldVisibility[field.id]
              : true;
          if (isVisible) {
            visibleFields.push(field);
          }
        }
      });
    };

    collectFields(fields);
    return visibleFields;
  };

  const validateForm = (fields, formData, fieldVisibility = {}, fieldRequired = {}) => {
    const errors = {};
    const fieldOrder = [];

    const visibleFields = getAllVisibleFields(fields, fieldVisibility);

    for (const field of visibleFields) {
      fieldOrder.push(field.id);
      
      const isRequired = fieldRequired[field.id] !== undefined ? fieldRequired[field.id] : field.required;
      const fieldValue = formData[field.id];

      const fieldToValidate = { ...field, required: isRequired };
      const validation = validateField(fieldToValidate, fieldValue);

      if (!validation.isValid) {
        if (validation.message === fieldValidationMessages.REQUIRED) {
          errors[field.id] = `${field.label} is required`;
        } else {
          errors[field.id] = `${field.label}: ${validation.message}`;
        }
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
      fieldOrder,
    };
  };

  return { validateField, validateForm, getAllVisibleFields };
}
